import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";

import { storage } from "@/lib/storage";
import { getJwtExpiryMs, isJwtExpired } from "@/lib/auth/token-provider";

interface AuthContextValue {
  isAuthenticated: boolean;
  setToken: (token: string) => void;
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** setTimeout's signed-32-bit ceiling (~24.8 days); longer delays overflow. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** User identity/settings are server state (the ["user"] query); never mirror them here. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() =>
    storage.getAuthToken()
  );

  useEffect(
    () => storage.onAuthTokenChange(() => setTokenState(storage.getAuthToken())),
    []
  );

  // Nothing re-renders at `exp`, so without this timer an idle page would stay
  // "signed in" until a request was rejected.
  useEffect(() => {
    if (!token) return;
    const expiresAt = getJwtExpiryMs(token);
    if (expiresAt === null) return;

    let timeoutId: number;
    // Re-arms rather than scheduling the whole remaining time at once:
    // setTimeout saturates past MAX_TIMEOUT_MS and would otherwise fire (and
    // sign the user out) immediately on a long-lived token.
    const arm = () => {
      const remaining = expiresAt - Date.now();
      if (remaining <= 0) {
        // Marked before the clear, which synchronously flips isAuthenticated
        // and lets the guard render /login; the notice has to already be
        // there by then.
        storage.markSessionExpired();
        storage.clearAuthSession();
        return;
      }
      timeoutId = window.setTimeout(arm, Math.min(remaining, MAX_TIMEOUT_MS));
    };
    arm();

    return () => window.clearTimeout(timeoutId);
  }, [token]);

  // Any session end (expiry, the interceptor rejecting it, a sign-out in
  // another tab) must not leave the previous session's server state cached for
  // whoever signs in next. useLogout only covers the explicit sign-out.
  const queryClient = useQueryClient();
  const hadToken = useRef(token !== null);
  useEffect(() => {
    if (hadToken.current && !token) {
      queryClient.clear();
    }
    hadToken.current = token !== null;
  }, [queryClient, token]);

  // Writes go through storage, which notifies the subscription above
  // synchronously, so that stays the single path that updates `token`.
  const setToken = useCallback((next: string) => storage.setAuthToken(next), []);
  const clearSession = useCallback(() => storage.clearAuthSession(), []);

  // A present-but-expired JWT is NOT a session: the guard routes to /login
  // immediately instead of mounting the dashboard and bouncing on the first 401.
  const isAuthenticated = !!token && !isJwtExpired(token);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setToken,
        clearSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthStore() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthStore must be used within an AuthProvider");
  }
  return context;
}
